import type { Comparison, ComparisonCell } from '@fleequid-secrets/shared';

// Reading a comparison: a value group is a letter in a colour; a key is missing where an environment has the section
// but not the key; a value is a risk where a protected environment shares it with one that is not (staging holding
// production's Stripe key). Empty values share nothing.

export const GROUP_COLORS: readonly string[] = ['#6da7ec', '#aba3f0', '#2bc48c', '#fab219', '#e26ab5', '#9aabc8'];
export const groupColor = (group: number): string => GROUP_COLORS[group % GROUP_COLORS.length] as string;
export const groupLetter = (group: number): string => (group < 26 ? String.fromCharCode(65 + group) : `${group + 1}`);

type Row = Comparison['rows'][number];

// For each cell of a row, whether it is a protected value shared outside protection (or the other side of it).
export function riskyCells(row: Row, environments: Comparison['environments']): boolean[] {
    const shared = (cell: ComparisonCell | null, index: number): boolean => {
        if (!cell || cell.empty) {
            return false;
        }
        const isProtected = environments[index]?.protected ?? false;

        return row.cells.some(
            (other, otherIndex) =>
                otherIndex !== index &&
                other !== null &&
                !other.empty &&
                other.group === cell.group &&
                (environments[otherIndex]?.protected ?? false) !== isProtected,
        );
    };

    return row.cells.map(shared);
}

// The environments whose section holds at least one key. A section still empty there is not "missing" every key: it
// is not set up yet, and its column says so instead.
export const populated = (comparison: Comparison): boolean[] =>
    comparison.environments.map((_, index) => comparison.rows.some((row) => row.cells[index]));

// Where the key is missing: an environment whose section holds keys, but not this one.
export const missingCells = (row: Row, filled: readonly boolean[]): boolean[] =>
    row.cells.map((cell, index) => cell === null && (filled[index] ?? false));

export interface ComparisonSummary {
    keys: number;
    // Keys missing somewhere the section exists, counted once per environment.
    missing: number;
    // Keys whose protected value is shared with an unprotected environment.
    risky: number;
    // Missing keys per environment, in the comparison's order.
    missingByEnvironment: number[];
}

export function summarize(comparison: Comparison): ComparisonSummary {
    const missingByEnvironment = comparison.environments.map(() => 0);
    const filled = populated(comparison);
    let risky = 0;
    for (const row of comparison.rows) {
        missingCells(row, filled).forEach((missing, index) => {
            if (missing) {
                missingByEnvironment[index] = (missingByEnvironment[index] ?? 0) + 1;
            }
        });
        if (riskyCells(row, comparison.environments).some(Boolean)) {
            risky += 1;
        }
    }

    return { keys: comparison.rows.length, missing: missingByEnvironment.reduce((sum, count) => sum + count, 0), risky, missingByEnvironment };
}
